import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, PhoneCall, PhoneOff, Users, TrendingUp, Calendar,
  BarChart2, Zap, Brain, Clock, RefreshCw, Play, Pause,
  CheckCircle, XCircle, AlertCircle, Mic, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceAIDashboard = () => {
  // Get business content for call scripts
  const { getCallScript, getBusinessTypeName } = useBusinessContent();
  const callScript = getCallScript();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [callAnalytics, setCallAnalytics] = useState(null);
  const [pendingRetries, setPendingRetries] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(7);

  useEffect(() => {
    fetchDashboardData();
    fetchCallAnalytics();
    fetchPendingRetries();
    fetchRecentCalls();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/voice-ai/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCallAnalytics = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/voice-ai/analytics/calls?days=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCallAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchPendingRetries = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/voice-ai/retries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRetries(res.data.pending_retries || []);
    } catch (error) {
      console.error('Failed to fetch retries:', error);
    }
  };

  const fetchRecentCalls = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/vapi/calls?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentCalls(res.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch recent calls:', error);
    }
  };

  const cancelRetry = async (leadId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/voice-ai/retries/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Retry cancelled');
      fetchPendingRetries();
    } catch (error) {
      toast.error('Failed to cancel retry');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-emerald-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  const today = dashboardData?.today || {};
  const analysis = dashboardData?.analysis || {};
  const crmUpdates = dashboardData?.crm_updates || {};
  const retryStats = dashboardData?.retries || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-emerald-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Brain className="w-8 h-8 text-emerald-400" />
              {getBusinessTypeName()} Voice AI
            </h1>
            <p className="text-emerald-300/70 mt-1">Smart AI calling with memory & automation</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(Number(e.target.value))}
              className="px-4 py-2 bg-emerald-900/30 border border-emerald-700/50 rounded-lg text-white"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <Button
              onClick={() => {
                fetchDashboardData();
                fetchCallAnalytics();
                toast.success('Dashboard refreshed');
              }}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            icon={<Phone className="w-6 h-6" />}
            label="Calls Today"
            value={today.calls_made || 0}
            color="blue"
          />
          <StatCard
            icon={<PhoneCall className="w-6 h-6" />}
            label="Answered"
            value={today.calls_answered || 0}
            color="green"
            subtext={`${today.answer_rate || 0}% rate`}
          />
          <StatCard
            icon={<MessageSquare className="w-6 h-6" />}
            label="AI Conversations"
            value={today.ai_conversations || 0}
            color="purple"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Interested Leads"
            value={today.interested_leads || 0}
            color="emerald"
          />
          <StatCard
            icon={<Calendar className="w-6 h-6" />}
            label="Meetings Booked"
            value={today.meetings_booked || 0}
            color="orange"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Conversion Rate"
            value={`${dashboardData?.conversion_rate || 0}%`}
            color="pink"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Call Analysis Stats */}
          <Card className="bg-emerald-900/20 border-emerald-700/30">
            <CardHeader>
              <CardTitle className="text-emerald-300 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                AI Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Calls Analyzed</span>
                <span className="text-white font-semibold">{analysis.total_analyzed || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Avg Call Score</span>
                <span className="text-white font-semibold">{analysis.average_score || 0}/100</span>
              </div>
              <div className="h-2 bg-emerald-950 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-green-400 rounded-full transition-all"
                  style={{ width: `${analysis.average_score || 0}%` }}
                />
              </div>

              <div className="pt-4 space-y-2">
                <h4 className="text-sm font-medium text-emerald-300">Interest Distribution</h4>
                <InterestBar label="High Interest" count={analysis.high_interest_count || 0} color="green" />
                <InterestBar label="Medium Interest" count={analysis.medium_interest_count || 0} color="yellow" />
                <InterestBar label="Low Interest" count={analysis.low_interest_count || 0} color="red" />
              </div>
            </CardContent>
          </Card>

          {/* CRM Updates */}
          <Card className="bg-emerald-900/20 border-emerald-700/30">
            <CardHeader>
              <CardTitle className="text-emerald-300 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                CRM Auto-Updates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CRMStatRow label="Interested" count={crmUpdates.interested || 0} color="green" icon={<CheckCircle />} />
              <CRMStatRow label="Follow Up" count={crmUpdates.follow_up || 0} color="blue" icon={<Clock />} />
              <CRMStatRow label="Nurturing" count={crmUpdates.nurturing || 0} color="yellow" icon={<Users />} />
              <CRMStatRow label="Lost" count={crmUpdates.lost || 0} color="red" icon={<XCircle />} />
              <CRMStatRow label="Unreachable" count={crmUpdates.unreachable || 0} color="gray" icon={<PhoneOff />} />
            </CardContent>
          </Card>

          {/* Retry Queue */}
          <Card className="bg-emerald-900/20 border-emerald-700/30">
            <CardHeader>
              <CardTitle className="text-emerald-300 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Retry Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-emerald-950/50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-400">{retryStats.pending || 0}</div>
                  <div className="text-xs text-gray-400">Pending</div>
                </div>
                <div className="text-center p-3 bg-emerald-950/50 rounded-lg">
                  <div className="text-2xl font-bold text-green-400">{retryStats.completed || 0}</div>
                  <div className="text-xs text-gray-400">Completed</div>
                </div>
              </div>

              {pendingRetries.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pendingRetries.slice(0, 5).map((retry) => (
                    <div key={retry.id} className="flex items-center justify-between p-2 bg-emerald-950/30 rounded">
                      <div>
                        <div className="text-sm text-white">Attempt {retry.attempt_number}</div>
                        <div className="text-xs text-gray-400">{formatDateTime(retry.scheduled_at)}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelRetry(retry.lead_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-sm">
                  No pending retries
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Calls Table */}
        <Card className="bg-emerald-900/20 border-emerald-700/30">
          <CardHeader>
            <CardTitle className="text-emerald-300 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              Recent AI Conversations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-400 text-sm border-b border-emerald-700/30">
                    <th className="pb-3 px-2">Lead</th>
                    <th className="pb-3 px-2">Time</th>
                    <th className="pb-3 px-2">Duration</th>
                    <th className="pb-3 px-2">Status</th>
                    <th className="pb-3 px-2">Interest</th>
                    <th className="pb-3 px-2">Score</th>
                    <th className="pb-3 px-2">Intent</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalls.map((call) => (
                    <tr key={call.id} className="border-b border-emerald-700/20 hover:bg-emerald-900/20">
                      <td className="py-3 px-2">
                        <div className="text-white font-medium">{call.lead_name || 'Unknown'}</div>
                        <div className="text-xs text-gray-400">{call.lead_phone}</div>
                      </td>
                      <td className="py-3 px-2 text-gray-300 text-sm">{formatDateTime(call.created_at)}</td>
                      <td className="py-3 px-2 text-gray-300">{formatDuration(call.duration)}</td>
                      <td className="py-3 px-2">
                        <StatusBadge status={call.status} />
                      </td>
                      <td className="py-3 px-2">
                        <InterestBadge level={call.analysis?.interest_level} />
                      </td>
                      <td className="py-3 px-2">
                        <ScoreBadge score={call.analysis?.call_score} />
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-gray-300 text-sm capitalize">
                          {call.analysis?.intent?.replace('_', ' ') || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {recentCalls.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        No calls yet. Start calling leads to see AI conversations here.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Daily Chart */}
        {callAnalytics?.daily_stats && callAnalytics.daily_stats.length > 0 && (
          <Card className="bg-emerald-900/20 border-emerald-700/30">
            <CardHeader>
              <CardTitle className="text-emerald-300 flex items-center gap-2">
                <BarChart2 className="w-5 h-5" />
                Call Trends ({selectedPeriod} Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 h-40">
                {callAnalytics.daily_stats.map((day, index) => {
                  const maxCalls = Math.max(...callAnalytics.daily_stats.map(d => d.total || 0), 1);
                  const height = ((day.total || 0) / maxCalls) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-1">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t min-h-[4px]"
                        title={`${day.total} calls`}
                      />
                      <span className="text-xs text-gray-500 truncate">
                        {day._id?.slice(5) || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ icon, label, value, color, subtext }) => {
  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400',
    pink: 'from-pink-500/20 to-pink-600/10 border-pink-500/30 text-pink-400'
  };

  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]} border`}>
      <CardContent className="p-4">
        <div className={`${colorClasses[color].split(' ').pop()} mb-2`}>{icon}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-sm text-gray-400">{label}</div>
        {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
      </CardContent>
    </Card>
  );
};

const InterestBar = ({ label, count, color }) => {
  const colors = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-400">{label}</span>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${colors[color]}`} />
        <span className="text-white font-medium">{count}</span>
      </div>
    </div>
  );
};

const CRMStatRow = ({ label, count, color, icon }) => {
  const colors = {
    green: 'text-green-400 bg-green-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
    red: 'text-red-400 bg-red-500/10',
    gray: 'text-gray-400 bg-gray-500/10'
  };

  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-950/30">
      <div className="flex items-center gap-2">
        <span className={`p-1.5 rounded ${colors[color]}`}>
          {React.cloneElement(icon, { className: 'w-4 h-4' })}
        </span>
        <span className="text-gray-300">{label}</span>
      </div>
      <span className="text-white font-semibold">{count}</span>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const configs = {
    completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
    in_progress: { color: 'bg-blue-100 text-blue-800', icon: <PhoneCall className="w-3 h-3" /> },
    no_answer: { color: 'bg-red-100 text-red-800', icon: <PhoneOff className="w-3 h-3" /> },
    failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-3 h-3" /> },
    calling: { color: 'bg-yellow-100 text-yellow-800', icon: <Phone className="w-3 h-3 animate-pulse" /> }
  };

  const config = configs[status] || { color: 'bg-gray-100 text-gray-800', icon: null };

  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      {config.icon}
      {status?.replace('_', ' ')}
    </Badge>
  );
};

const InterestBadge = ({ level }) => {
  if (!level) return <span className="text-gray-500">-</span>;

  const colors = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-orange-100 text-orange-800',
    not_interested: 'bg-red-100 text-red-800'
  };

  return (
    <Badge className={colors[level] || 'bg-gray-100 text-gray-800'}>
      {level?.replace('_', ' ')}
    </Badge>
  );
};

const ScoreBadge = ({ score }) => {
  if (score === undefined || score === null) return <span className="text-gray-500">-</span>;

  let color = 'text-gray-400';
  if (score >= 80) color = 'text-green-400';
  else if (score >= 60) color = 'text-yellow-400';
  else if (score >= 40) color = 'text-orange-400';
  else color = 'text-red-400';

  return <span className={`font-semibold ${color}`}>{score}/100</span>;
};

export default VoiceAIDashboard;
